import { useState } from 'react';
import {
   Flex,
   Text,
   Input,
   InputGroup,
   InputRightElement,
   FormLabel,
   InputLeftElement,
} from '@chakra-ui/react';
import { useLocalization } from '~/framework';
import { useDebounced } from '@dozuki/web-js/components';
import { useCourse, useCourses } from '~/pages/Courses/_shared';
import {
   Catalog,
   CatalogItem,
   CatalogItemContent,
   CatalogItemHeader,
   Icon,
   InfiniteScrollForIncrementalQuery,
   SearchForm,
} from '~/ui-components';
import { faEdit, faLoader } from '@fortawesome/pro-regular-svg-icons';
import { CourseAssignmentOptions } from './CourseAssignmentOptions';

type AssignmentCourseInputProps = {
   assignment: CourseAssignmentOptions;
   onChange: (assignment: CourseAssignmentOptions) => void;
};

export function AssignmentCourseInput({ assignment, onChange }: AssignmentCourseInputProps) {
   const { _js } = useLocalization();

   const [searchTerm, setSearchTerm] = useState('');

   const debouncedSearchTerm = useDebounced(searchTerm, 500);

   const courseQuery = useCourses({
      query: debouncedSearchTerm,
      pageSize: 10,
   });

   const { flattenedData: courses } = courseQuery;

   const { data: assignedCourse } = useCourse(assignment.wikiid);

   return assignment.wikiid !== null ? (
      <>
         <FormLabel>{_js('Course')}</FormLabel>
         <InputGroup
            aria-label={_js('Edit Course')}
            cursor="pointer"
            onClick={() =>
               onChange({
                  ...assignment,
                  wikiid: null,
               })
            }
         >
            {!assignedCourse?.title && (
               <InputLeftElement>
                  <Icon type={faLoader} spin />
               </InputLeftElement>
            )}
            <Input readOnly pointerEvents="none" value={assignedCourse?.title || ''} />;
            <InputRightElement>
               <Icon type={faEdit} />
            </InputRightElement>
         </InputGroup>
      </>
   ) : (
      <>
         <Text variant="muted">{_js('Select a course to assign')}:</Text>
         <SearchForm
            placeholder={_js('Search Courses')}
            name="search-courses"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
         />
         <InfiniteScrollForIncrementalQuery query={courseQuery} contained height={300}>
            {courses?.length === 0 && (
               <Flex flex={1} flexDirection="column" align="center" justify="center">
                  <Text variant="subtitle">{_js('There are no courses to display')}</Text>
                  {debouncedSearchTerm.length > 3 && (
                     <Text variant="muted">{_js('Try searching for something different.')}</Text>
                  )}
               </Flex>
            )}
            <Catalog>
               {courses?.map(course => (
                  <CatalogItem
                     key={course.wikiid}
                     onClick={() => onChange({ ...assignment, wikiid: course.wikiid })}
                  >
                     <CatalogItemContent>
                        <CatalogItemHeader>{course.title}</CatalogItemHeader>
                     </CatalogItemContent>
                  </CatalogItem>
               ))}
            </Catalog>
         </InfiniteScrollForIncrementalQuery>
      </>
   );
}
